import React, { useContext, useEffect } from 'react'
import { toast } from 'react-toastify'
import { GoogleLogin, useGoogleLogin } from '@react-oauth/google'
import axios from 'axios'
import { useHistory } from 'react-router-dom'
import { UserContext } from '../../contexts/UserContext'
import { getBackendUserUrl, getGoogleClientId } from 'utils/utils'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'

const handleMailjetSubmit = async (email) => {
    try {
        const apiUrl = getBackendUserUrl() + '/mailjet/add_contact'
        const response = await axios.post(
            apiUrl,
            { email: email },
            {
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        )

        if (response.status === 200) {
            console.log('Submitted email address to mailjet')
        } else {
            console.error(
                'Error adding email to waitlist: ',
                response.statusText
            )
            alert('An error occurred. Please try again later.')
        }
    } catch (error) {
        console.error(
            'Error adding email to waitlist: ',
            error.response?.data || error.message
        )
        alert('An error occurred. Please try again later.')
    }
}

const GoogleSignIn = ({ onUserLogin }) => {
    const { login } = useContext(UserContext)
    const history = useHistory()

    const clientId = getGoogleClientId()

    const fetchGoogleAccessToken = useGoogleLogin({
        onSuccess: (tokenResponse) => {
            const accessToken = tokenResponse.access_token
            localStorage.setItem('googleAccessToken', accessToken)
            console.log('Google Drive access token stored.')
        },
        onError: (error) => {
            console.error('Error fetching access token:', error)
        },
        scope: 'https://www.googleapis.com/auth/drive.readonly',
        flow: 'implicit',
    })

    if (!clientId) {
        console.error('Google Client ID is not configured')
        return <div>Authentication configuration error</div>
    }

    const handleGoogleLogin = async (credentialResponse) => {
        try {
            console.log('Google login response received')

            if (!credentialResponse.credential) {
                console.error('No credential received from Google')
                toast.error('Google sign-in failed. Please try again.')
                return
            }

            const inviteCompanyId = localStorage.getItem('inviteCompanyId');
            const inviteEventId = localStorage.getItem('inviteEventId');
            const authUrl = getBackendUserUrl() + '/user/auth/google';

            // First API call - Authentication
            const authPayload = {
                googleToken: credentialResponse.credential,
            };

            if (inviteCompanyId) {
                authPayload.companyId = inviteCompanyId;
            }

            const authResponse = await axios.post(
                authUrl,
                authPayload,
                {
                    headers: { 'Content-Type': 'application/json' },
                }
            );

            const { token, user } = authResponse.data;

            // Second API call - Add user as participant if eventId exists
            if (inviteEventId) {
                try {
                    const participantsUrl = getBackendUserUrl() + `/event/${inviteEventId}/add-participants`;
                    const invitedBy = localStorage.getItem('invitedBy');
                    const participantsPayload = {
                        userIds: [user.userId],
                        invited_by: invitedBy || undefined,
                        role: "participant"
                    };

                    console.log('Adding participant with payload:', participantsPayload);
                    
                    const response = await axios.post(
                        participantsUrl,
                        participantsPayload,
                        {
                            headers: { 
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${token}`
                            },
                        }
                    );

                    console.log('Participant addition response:', response.data);

                    // Clean up localStorage
                    localStorage.removeItem('invitedBy');
                } catch (participantError) {
                    console.error('Error adding user as participant:', {
                        status: participantError.response?.status,
                        data: participantError.response?.data,
                        error: participantError.message
                    });
                }
            }

            // Clean up localStorage
            if (inviteCompanyId) {
                localStorage.removeItem('inviteCompanyId');
            }
            if (inviteEventId) {
                localStorage.removeItem('inviteEventId');
            }

            // TODO: Update role to use new Supabase enum
            if (
                user.role &&
                (user.role.includes('user') || user.role.includes('admin'))
            ) {
                login(user, token) // This is where we set the user token

                fetchGoogleAccessToken()
                window.location.href = '/myevents';
            } else {
                if (onUserLogin) {
                    onUserLogin({
                        success: true,
                        message:
                            "Thank you for requesting beta access. We'll notify you once approved.",
                    })
                }
                await handleMailjetSubmit(user.email)
                history.push('/')
            }
        } catch (error) {
            console.error('Error during Google Sign-In:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status,
            })

            if (error.response?.status === 500) {
                toast.error('Server error. Please try again later.')
            } else if (error.response?.status === 401) {
                toast.error('Authentication failed. Please try again.')
            } else {
                toast.error(
                    `Login failed: ${error.response?.data?.detail || 'Please try again'}`
                )
            }
        }
    }

    return (
        <>
            <GoogleLogin
                onSuccess={handleGoogleLogin}
                onError={(error) => {
                    console.error('Google Login Error:', error)
                    toast.error('Login failed. Please try again.')
                }}
                useOneTap={false}
                theme="outline"
                size="large"
                text="signin_with"
                shape="rectangular"
            />
            <ToastContainer
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
            />
        </>
    )
}

export default GoogleSignIn
